import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useFormState } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { applySlabSurfaceListEdits, buildSlabSurfaceGuidToBeamGuid } from '@domainUtils'
import { AxiosError } from 'axios'
import { keyBy } from 'lodash-es'
import { useDebouncedCallback } from 'use-debounce'
import { useModelStore, useResultsStore } from '@editor/stores'
import { useMutation } from '@tanstack/react-query'
import { enqueueSnackbar } from '@ui/snackbar'
import { savePositionGrouping } from '@mutations'
import useSyncLinkedPlanCommentText from '../../../hooks/useSyncLinkedPlanCommentText'
import type { Results2ListItem } from '../types'

/**
 * Element types whose lists support position grouping, mapped to the server grouping type.
 * The Decke/Dach lists show slab *surfaces*, which have no grouping of their own — their
 * "Exportiert" flag lives on the bundle of the surface's slab beam, so they map to the beam
 * grouping (see `applySlabSurfaceExportFlags`).
 */
const POSITION_GROUPING_TYPE_BY_ELEMENT_TYPE: Partial<Record<ElementTypes, PositionGroupingType>> =
  {
    slab_beams: 'slab-beams',
    roof_slab_beams: 'roof-slab-beams',
    slabs: 'slab-beams',
    roof_slabs: 'roof-slab-beams',
    lintels: 'wall-lintels',
    rips: 'wall-rips',
    beams: 'beams',
    purlins: 'purlins',
    columns: 'columns',
  }

/** Rebuilds the grouping from the list rows: top-level rows are bundles, nested rows their children. */
const buildBundlesFromRows = (
  items: Results2ListItem[],
  existingByRepresentative: Record<string, MemberPositionBundle>,
): MemberPositionBundle[] => {
  const childrenByParent = new Map<string, Results2ListItem[]>()
  for (const item of items) {
    if (!item.parent) continue
    const children = childrenByParent.get(item.parent) ?? []
    children.push(item)
    childrenByParent.set(item.parent, children)
  }

  return items
    .filter(item => !item.parent)
    .map(item => {
      const existing = existingByRepresentative[item.id]
      return {
        ...(existing ?? {
          representative_position: item.id,
          representative_for: [],
          exported: false,
        }),
        representative_for: (childrenByParent.get(item.id) ?? []).map(child => child.id),
        exported: !!item.ok,
        comment: item.comment,
        extended_comment: item.extended_comment,
        notes: existing?.notes ?? '',
      }
    })
}

type AutosavePayload = {
  items: Results2ListItem[]
  signal: AbortSignal
}

const isAbortError = (error: unknown): boolean => {
  const axiosError = error as AxiosError
  return axiosError.code === 'ERR_CANCELED' || axiosError.message === 'canceled'
}

type UsePositionGroupingAutosaveProps = {
  /** Live form items — every change while the form is dirty re-arms the debounced save. */
  items: Results2ListItem[]
  /** First selected element; its type determines which position grouping the list edits. */
  elementType: ElementTypes
  disableForm: boolean
}

type UsePositionGroupingAutosaveResult = {
  isSaving: boolean
  dirty: boolean
  /** Run a pending debounced save immediately (save icon, unmount). */
  flushAutosave: () => void
}

/**
 * Debounced, abortable autosave of grouping/comment/export edits. The server response is mirrored
 * back into the results store so other consumers (detail pane, export) see the saved state.
 */
export const usePositionGroupingAutosave = ({
  items,
  elementType,
  disableForm,
}: UsePositionGroupingAutosaveProps): UsePositionGroupingAutosaveResult => {
  const { projectId } = useParams<{ projectId: string }>()

  const positionGroupingType = elementType
    ? POSITION_GROUPING_TYPE_BY_ELEMENT_TYPE[elementType]
    : undefined

  const existingGrouping = useResultsStore(state => {
    switch (elementType) {
      case 'slab_beams':
      case 'slabs':
        return state.slabBeamsPositionGrouping
      case 'roof_slab_beams':
      case 'roof_slabs':
        return state.roofSlabBeamsPositionGrouping
      case 'lintels':
        return state.wallLintelsPositionGrouping
      case 'rips':
        return state.wallRipsPositionGrouping
      case 'beams':
        return state.beamsPositionGrouping
      case 'purlins':
        return state.purlinsPositionGrouping
      case 'columns':
        return state.columnsPositionGrouping
      case 'standard_rips':
        return null
      default:
        throw new Error(`Unsupported reference element type: ${elementType}`)
    }
  })

  const setWallRipsPositionGrouping = useResultsStore(state => state.setWallRipsPositionGrouping)
  const setWallLintelsPositionGrouping = useResultsStore(
    state => state.setWallLintelsPositionGrouping,
  )
  const setSlabBeamsPositionGrouping = useResultsStore(state => state.setSlabBeamsPositionGrouping)
  const setRoofSlabBeamsPositionGrouping = useResultsStore(
    state => state.setRoofSlabBeamsPositionGrouping,
  )
  const setBeamsPositionGrouping = useResultsStore(state => state.setBeamsPositionGrouping)
  const setPurlinsPositionGrouping = useResultsStore(state => state.setPurlinsPositionGrouping)
  const setColumnsPositionGrouping = useResultsStore(state => state.setColumnsPositionGrouping)

  const setGroupingForType = useCallback(
    (type: ElementTypes, data: MemberPositionBundle[]) => {
      if (type === 'rips') setWallRipsPositionGrouping(data)
      else if (type === 'lintels') setWallLintelsPositionGrouping(data)
      else if (type === 'slab_beams' || type === 'slabs') setSlabBeamsPositionGrouping(data)
      else if (type === 'roof_slab_beams' || type === 'roof_slabs')
        setRoofSlabBeamsPositionGrouping(data)
      else if (type === 'beams') setBeamsPositionGrouping(data)
      else if (type === 'purlins') setPurlinsPositionGrouping(data)
      else if (type === 'columns') setColumnsPositionGrouping(data)
    },
    [
      setBeamsPositionGrouping,
      setColumnsPositionGrouping,
      setPurlinsPositionGrouping,
      setRoofSlabBeamsPositionGrouping,
      setSlabBeamsPositionGrouping,
      setWallLintelsPositionGrouping,
      setWallRipsPositionGrouping,
    ],
  )

  // Decke/Dach rows are slab surfaces; their exported flag is stored on the bundle of the
  // surface's slab beam, so the save path needs the surface -> beam guid mapping.
  const isSlabSurfaceList = elementType === 'slabs' || elementType === 'roof_slabs'
  const verticalSlabs = useModelStore(state => state.model.vertical_slabs)
  const verticalRoofSlabs = useModelStore(state => state.model.vertical_roof_slabs)
  const surfaceGuidToBeamGuid = useMemo(
    () =>
      buildSlabSurfaceGuidToBeamGuid(
        elementType === 'roof_slabs' ? verticalRoofSlabs : verticalSlabs,
      ),
    [elementType, verticalRoofSlabs, verticalSlabs],
  )

  const dirty = useResultsStore(state => state.elementListGroupingDirty)
  const setElementListGroupingDirty = useResultsStore(state => state.setElementListGroupingDirty)
  const autosaveAbortControllerRef = useRef<AbortController | null>(null)

  const syncLinkedPlanCommentText = useSyncLinkedPlanCommentText()

  const { mutate: mutateAutosave, isLoading: isSaving } = useMutation({
    mutationKey: savePositionGrouping.getKey(
      projectId,
      positionGroupingType as PositionGroupingType,
    ),
    mutationFn: async ({ items: nextItems, signal }: AutosavePayload) => {
      if (!projectId || !existingGrouping || !positionGroupingType) return

      const existingByRepresentative = keyBy(existingGrouping, 'representative_position')

      // Surface rows are not positions themselves: they only edit the bundle of their slab beam,
      // so the stored grouping is kept and the edited fields are written onto it.
      const bundles = isSlabSurfaceList
        ? applySlabSurfaceListEdits(existingGrouping, nextItems, surfaceGuidToBeamGuid)
        : buildBundlesFromRows(nextItems, existingByRepresentative)

      const data = await savePositionGrouping.request(
        projectId,
        positionGroupingType,
        bundles,
        signal,
      )

      // After a successful save, mirror every changed Positionsliste text into the element's
      // linked Positionsplan comment (linked comments are keyed by the representative element).
      for (const bundle of bundles) {
        const existing = existingByRepresentative[bundle.representative_position]
        if ((bundle.comment ?? '') !== (existing?.comment ?? '')) {
          syncLinkedPlanCommentText(bundle.representative_position, bundle.comment ?? '')
        }
      }

      return data
    },
    onSuccess: (data: MemberPositionBundle[] | undefined, variables: AutosavePayload) => {
      // Ignore responses of aborted/superseded saves — only the latest request may win.
      if (autosaveAbortControllerRef.current?.signal !== variables.signal || !data) return

      setGroupingForType(elementType, data)
    },
    onError: (error: unknown) => {
      if (isAbortError(error)) return

      enqueueSnackbar(
        buildErrorMessage(error as AxiosError, 'Fehler beim Speichern der Gruppierung'),
        { variant: 'error' },
      )
    },
    onSettled: (_data, _error, variables: AutosavePayload) => {
      // Clear the optimistic dirty flag once the latest save settles — on success OR error,
      // including the early-return (undefined data) path. Doing this only in `onSuccess`
      // behind the `!data` guard left the flag stuck `true` on any failed/no-op save, which
      // permanently disabled the detail form (only a reload cleared it). A superseded save
      // (a newer edit already swapped the ref) must not clear it — the newer save owns the reset.
      if (autosaveAbortControllerRef.current?.signal === variables.signal) {
        setElementListGroupingDirty(false)
        autosaveAbortControllerRef.current = null
      }
    },
  })

  const debouncedAutosave = useDebouncedCallback(mutateAutosave, 1000)

  const { dirtyFields } = useFormState()

  useEffect(() => {
    if (disableForm || !('items' in dirtyFields)) return

    autosaveAbortControllerRef.current?.abort()
    const autosaveAbortController = new AbortController()
    autosaveAbortControllerRef.current = autosaveAbortController

    setElementListGroupingDirty(true)
    debouncedAutosave({
      items,
      signal: autosaveAbortController.signal,
    })
  }, [disableForm, setElementListGroupingDirty, items, dirtyFields, debouncedAutosave])

  // Flush a pending save when the list unmounts (e.g. tab switch).
  useEffect(() => {
    return () => {
      debouncedAutosave.flush()
      // The list is unmounting — it can no longer own the "dirty" disable, and the mutation's
      // settle callbacks won't run once this observer is torn down. Any save the flush just
      // kicked off keeps the detail form disabled via `isSavingPositionGrouping` (a global
      // mutation counter) until it settles, so clearing the flag here is safe and prevents a
      // stuck-disabled detail form when the list unmounts mid-save.
      setElementListGroupingDirty(false)
    }
  }, [debouncedAutosave, setElementListGroupingDirty])

  return { isSaving, dirty, flushAutosave: debouncedAutosave.flush }
}
